<template>
  <VueDatePicker
    v-model="datePickerModelValue"
    :class="`${DS_DATEPICKER_CLASSES.Root} ${DS_DATEPICKER_CLASSES.StartOfWeekBase}${startOfWeek || '1'}`"
    data-cy="ds-date-picker__calendar"
    inline
    :esc-close="false"
    :enable-time-picker="false"
    auto-apply
    :week-start="startOfWeek"
    :disabled="props.disabled"
    :disabled-dates="disabledDatesForVueDatePicker"
  >
    <template
      v-if="pickerMode === DS_DATEPICKER_CALENDAR_MODES.Date"
      #action-extra
    >
      <DSButton
        data-cy="ds-datepicker__today-button"
        size="sm"
        variant="secondary"
        @click="setToday"
      >
        {{ buttonTodayText }}
      </DSButton>
    </template>

    <template #month-year="{ month, year, years, months, updateMonthYear }">
      <DSDatepickerHeader
        v-model:picker-mode="pickerMode"
        :month="month ?? dayjs().month()"
        :year="year ?? dayjs().year()"
        :months="months"
        :years="years"
        :update-month-year="updateMonthYear"
      />
    </template>

    <template #day="slot">
      <DSDatepickerDayButton
        :date="slot.date"
        :day-label="slot.day"
        :disabled="disabledDatesForVueDatePicker(slot.date)"
        :selected-date="modelValue"
      />
    </template>
  </VueDatePicker>
</template>

<script setup lang="ts">
import VueDatePicker from "@vuepic/vue-datepicker";
import { computed, ref } from "vue";
import dayjs from "dayjs";
import DSDatepickerHeader from "@/src/design_system/form_elements/datepicker/components/DSDatepickerHeader.vue";
import DSDatepickerDayButton from "@/src/design_system/form_elements/datepicker/components/DSDatepickerDayButton.vue";
import { easySettings } from "@/src/shared/store/easyGlobalSettings";
import {
  DS_DATEPICKER_CALENDAR_MODES,
  DSDatepickerCalendarMode,
  DSDatePickerCalendarProps,
} from "@/src/design_system/form_elements/datepicker/types/calendar";
import type { DSDateTimePickerModelValue } from "@/src/design_system/form_elements/shared/types/dateTimePicker";
import { DS_DATEPICKER_CLASSES } from "@/src/design_system/form_elements/datepicker/constants/classes";
import { DSButton } from "@/src/design_system";

const props = defineProps<DSDatePickerCalendarProps>();

const buttonTodayText = window.I18n.designSystem.datePicker.buttonToday;

const setToday = () => {
  modelValue.value = dayjs();
};

const pickerMode = ref<DSDatepickerCalendarMode>(DS_DATEPICKER_CALENDAR_MODES.Date);

const modelValue = defineModel<DSDateTimePickerModelValue>({ required: true });

const datePickerModelValue = computed({
  get: () => (modelValue.value ? modelValue.value.toDate() : null),
  set: (val: Date | null) => {
    modelValue.value = val ? dayjs(val) : null;
  },
});

const startOfWeek = computed(
  () =>
    (easySettings.getStartOfWeek.value >= 0 ? easySettings.getStartOfWeek.value : undefined) as
      | 0
      | 1
      | 2
      | 3
      | 4
      | 5
      | 6
      | undefined
);

const disabledDatesForVueDatePicker = (date: Date) => {
  if (!props.disabledDates) return false;

  const dateDayjs = dayjs(date);

  return props.disabledDates(dateDayjs);
};
</script>
